import React, { useState } from 'react';
import { Play, Clock, Eye, Calendar, Search, Filter, ChevronRight } from 'lucide-react';
import { mockData } from '../mock';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const VideoLibrary = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const categories = [
    'All',
    'Sunya Talks',
    'Ganotherapy',
    'Shang Han Lun',
    'Herbology',
    'Herbal Talk',
    'Coffee Talk',
    'Charla de Café',
    'Hexageneous Reflexology'
  ];

  const filteredVideos = mockData.latestTalks.filter(talk => {
    const matchesCategory = selectedCategory === 'All' || talk.category === selectedCategory;
    const matchesSearch = talk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         talk.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredVideos = mockData.latestTalks.filter(talk => talk.featured);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 pt-20">
      <SEOHead 
        title="Video Library - My Talks"
        description="Watch Dr. Lim Siow Jin's video talks on Sunya meditation, Ganotherapy, Shang Han Lun, Traditional Chinese Medicine, and wellness. Netflix-style video library with 100+ educational talks."
        keywords="Dr. Lim Siow Jin videos, Sunya talks, Ganotherapy videos, Shang Han Lun teachings, TCM videos, meditation videos, wellness education"
        
      />
      
      {/* Hero Section with Featured Video */}
      {featuredVideos[0] && !selectedVideo && (
        <div className="relative h-[70vh] overflow-hidden">
          <img
            src={featuredVideos[0].thumbnail}
            alt={featuredVideos[0].title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <span className="inline-block bg-emerald-600 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                Featured
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                {featuredVideos[0].title}
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mb-6">
                {featuredVideos[0].description}
              </p>
              <div className="flex items-center space-x-6 text-sm text-gray-300 mb-6">
                <div className="flex items-center space-x-2">
                  <Eye size={18} />
                  <span>{featuredVideos[0].views} views</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock size={18} />
                  <span>{featuredVideos[0].duration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar size={18} />
                  <span>{new Date(featuredVideos[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedVideo(featuredVideos[0])}
                className="bg-white text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-gray-200 transition-all flex items-center space-x-3 group"
              >
                <Play size={24} fill="currentColor" />
                <span>Watch Now</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="sticky top-20 z-40 bg-slate-900/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-3 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <div className="max-w-6xl w-full">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">{selectedVideo.title}</h2>
              <button
                onClick={() => setSelectedVideo(null)}
                className="text-white hover:text-emerald-400 text-3xl"
              >
                ×
              </button>
            </div>
            <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-xl"
              ></iframe>
            </div>
            <div className="mt-4 text-gray-300">
              <div className="flex items-center space-x-6 text-sm mb-4">
                <div className="flex items-center space-x-2">
                  <Eye size={16} />
                  <span>{selectedVideo.views} views</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock size={16} />
                  <span>{selectedVideo.duration}</span>
                </div>
                <span className="px-3 py-1 bg-emerald-600 rounded-full text-xs font-semibold text-white">
                  {selectedVideo.category}
                </span>
              </div>
              <p className="text-gray-400">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Video Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            {selectedCategory === 'All' ? 'All Videos' : selectedCategory}
          </h2>
          <p className="text-gray-400">
            {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'} available
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              onClick={() => setSelectedVideo(video)}
              className="group cursor-pointer"
            >
              <div className="relative h-48 rounded-xl overflow-hidden mb-3">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="text-emerald-700" size={24} fill="currentColor" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm px-2 py-1 rounded text-white text-xs flex items-center space-x-1">
                  <Clock size={12} />
                  <span>{video.duration}</span>
                </div>
                {video.featured && (
                  <div className="absolute top-2 left-2 bg-emerald-600 px-2 py-1 rounded text-white text-xs font-semibold">
                    Featured
                  </div>
                )}
              </div>
              <div>
                <span className="text-xs font-semibold text-emerald-400 mb-1 block">{video.category}</span>
                <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-emerald-400 transition-colors">
                  {video.title}
                </h3>
                <p className="text-sm text-gray-400 line-clamp-2 mb-2">{video.description}</p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Eye size={12} />
                    <span>{video.views}</span>
                  </div>
                  <span>•</span>
                  <span>{new Date(video.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Continue Watching Section */}
        {selectedCategory === 'All' && featuredVideos.length > 1 && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Featured Talks</h3>
              <button className="text-emerald-400 hover:text-emerald-300 flex items-center space-x-1 text-sm font-semibold">
                <span>View All</span>
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredVideos.slice(1).map((video) => (
                <div
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  className="group cursor-pointer"
                >
                  <div className="relative h-36 rounded-lg overflow-hidden mb-2">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                        <Play className="text-emerald-700" size={18} fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <h4 className="font-semibold text-white text-sm line-clamp-2 group-hover:text-emerald-400 transition-colors">
                    {video.title}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoLibrary;
